'use client';

import { useTranslation } from 'react-i18next';
import { useUploadedFileStore } from '../../../stores/uploadedFileStore';
import { useWorkflowStore } from '../../../stores/workflowStore';
import { useSchema } from '../../../contexts/SchemaContext';
import { LoadExcelConfig } from './LoadExcelConfig';
import { LoadCSVConfig } from './LoadCSVConfig';

interface LoadTableConfigProps {
  nodeId: string;
  config: Record<string, unknown>;
}

export function LoadTableConfig({ nodeId, config }: LoadTableConfigProps) {
  const { t } = useTranslation();
  const { updateNodeConfig } = useWorkflowStore();
  const { clearFileForNode } = useUploadedFileStore();
  const { unregisterNodeSchema } = useSchema();

  const tableFormat = config.tableFormat === 'csv' ? 'csv' : 'excel';

  const setFormat = (fmt: 'excel' | 'csv') => {
    if (fmt === tableFormat) return;
    clearFileForNode(nodeId);
    unregisterNodeSchema(nodeId);
    if (fmt === 'excel') {
      updateNodeConfig(nodeId, {
        type: 'load_table',
        params: { tableFormat: 'excel', headerRow: 1 },
      });
    } else {
      updateNodeConfig(nodeId, {
        type: 'load_table',
        params: { tableFormat: 'csv', separator: ',', encoding: 'utf-8', hasHeader: true },
      });
    }
  };

  return (
    <div className="space-y-5">
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-zinc-500">{t('nodes.config.load.tableFormat')}</label>
        <select
          value={tableFormat}
          onChange={(e) => setFormat(e.target.value === 'csv' ? 'csv' : 'excel')}
          className="w-full text-sm bg-white border border-zinc-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
        >
          <option value="excel">{t('nodes.config.load.tableFormatExcel')}</option>
          <option value="csv">{t('nodes.config.load.tableFormatCsv')}</option>
        </select>
      </div>

      {tableFormat === 'excel' ? (
        <LoadExcelConfig nodeId={nodeId} config={config} persistType="load_table" paramDefaults={{ tableFormat: 'excel' }} />
      ) : (
        <LoadCSVConfig nodeId={nodeId} config={config} persistType="load_table" paramDefaults={{ tableFormat: 'csv' }} />
      )}
    </div>
  );
}
